import { create } from "zustand";
import { STORAGE_KEYS } from "@/constants/storageKeys";
import { ROUTES } from "@/config/routes";
import { apiClient } from "@/lib/api";
import { authApi } from "@/lib/authApi";
import { callApi } from "@/lib/callApi";
import { releaseEndedOrder } from "@/lib/anchorOrderRelease";
import { releaseOutgoingCall } from "@/lib/outgoingCallLifecycle";
import { presenceApi } from "@/lib/presenceApi";
import { realtimeClient } from "@/lib/realtimeClient";
import { getEnvConfig } from "@/lib/runtime-env";
import { shengwangClient } from "@/lib/shengwangClient";
import { useCallStore } from "./callStore";
import type { AnchorProfile } from "@/types";

interface AuthState {
  anchor: AnchorProfile | null;
  accessToken: string | null;
  refreshToken: string | null;
  sessionId: string | null;
  loading: boolean;
  error: string | null;
  restored: boolean;
  isAuthenticated: boolean;
  logout: () => Promise<void>;
  restore: () => void;
  handleAuthFailure: () => void;
  recoverAuthSession: () => Promise<string | null>;
  refreshSession: (options?: {
    reconnectRealtime?: boolean;
  }) => Promise<string | null>;
  updateAnchor: (anchor: Partial<AnchorProfile>) => void;
  clearLocalSession: () => void;
  setSession: (payload: {
    anchor: AnchorProfile;
    accessToken: string;
    refreshToken: string;
    sessionId: string;
  }) => void;
}

function startRealtimeConnection(accessToken: string) {
  const realtimeURL = getEnvConfig().REALTIME_BASE_URL;
  if (!realtimeURL) {
    realtimeClient.disconnect();
    return;
  }

  realtimeClient.connect({
    url: realtimeURL,
    token: accessToken,
  });
}

function syncRealtimeConnection(
  anchor: AnchorProfile | null,
  accessToken: string | null,
) {
  if (!anchor?.profile_completed || !accessToken) {
    realtimeClient.disconnect();
    return;
  }
  startRealtimeConnection(accessToken);
}

function clearStoredSession() {
  realtimeClient.disconnect();
  apiClient.clearToken();
  localStorage.removeItem(STORAGE_KEYS.refreshToken);
  localStorage.removeItem(STORAGE_KEYS.sessionId);
  localStorage.removeItem(STORAGE_KEYS.anchorProfile);
  useCallStore.getState().clearCalls();
}

export const useAuthStore = create<AuthState>((set, get) => ({
  anchor: null,
  accessToken: null,
  refreshToken: null,
  sessionId: null,
  loading: false,
  error: null,
  restored: false,
  isAuthenticated: false,

  setSession: ({ anchor, accessToken, refreshToken, sessionId }) => {
    apiClient.setToken(accessToken);
    localStorage.setItem(STORAGE_KEYS.refreshToken, refreshToken);
    localStorage.setItem(STORAGE_KEYS.sessionId, sessionId);
    localStorage.setItem(STORAGE_KEYS.anchorProfile, JSON.stringify(anchor));

    set({
      anchor,
      accessToken,
      refreshToken,
      sessionId,
      isAuthenticated: true,
      loading: false,
      error: null,
      restored: true,
    });
    syncRealtimeConnection(anchor, accessToken);
  },

  logout: async () => {
    const calls = useCallStore.getState();
    const currentCall = calls.currentCall;
    const outgoingCall = calls.outgoingCall;
    const endingCallOrderID = calls.endingCallOrderID;
    const anchorID = get().anchor?.id;
    if (currentCall?.order_id && !endingCallOrderID) {
      calls.setEndingCallOrderID(currentCall.order_id);
    }
    await shengwangClient.leaveCall();
    if (outgoingCall?.order_id && !currentCall) {
      await releaseOutgoingCall(
        outgoingCall.order_id,
        "call_canceled",
        "anchor_logout",
      );
    } else if (
      currentCall?.order_id &&
      endingCallOrderID !== currentCall.order_id &&
      useCallStore.getState().endingCallOrderID === currentCall.order_id
    ) {
      await releaseEndedOrder(currentCall.order_id, "anchor_logout");
    } else if (currentCall?.call_id) {
      await callApi.endCall(currentCall.call_id).catch(() => undefined);
    }
    useCallStore.getState().clearCalls();
    if (anchorID) {
      await presenceApi
        .heartbeat({ anchor_id: anchorID, online_status: 0 })
        .catch(() => undefined);
    }
    await authApi.logout().catch(() => undefined);
    clearStoredSession();

    set({
      anchor: null,
      accessToken: null,
      refreshToken: null,
      sessionId: null,
      isAuthenticated: false,
      loading: false,
      error: null,
      restored: true,
    });
  },

  handleAuthFailure: () => {
    clearStoredSession();
    set({
      anchor: null,
      accessToken: null,
      refreshToken: null,
      sessionId: null,
      isAuthenticated: false,
      loading: false,
      error: null,
      restored: true,
    });
    if (
      typeof window !== "undefined" &&
      window.location.pathname !== ROUTES.login
    ) {
      window.location.replace(ROUTES.login);
    }
  },

  clearLocalSession: () => {
    clearStoredSession();
    set({
      anchor: null,
      accessToken: null,
      refreshToken: null,
      sessionId: null,
      isAuthenticated: false,
      loading: false,
      error: null,
      restored: true,
    });
  },

  recoverAuthSession: async () => {
    const token = await get().refreshSession({ reconnectRealtime: false });
    if (token) {
      return token;
    }
    get().handleAuthFailure();
    return null;
  },

  refreshSession: async (options) => {
    const refreshToken =
      get().refreshToken || localStorage.getItem(STORAGE_KEYS.refreshToken);
    if (!refreshToken) {
      return null;
    }
    try {
      const response = await authApi.refresh({ refresh_token: refreshToken });
      const data = response.data;
      if (
        !data?.access_token ||
        !data.refresh_token ||
        !data.session_id ||
        !data.anchor
      ) {
        return null;
      }
      apiClient.setToken(data.access_token);
      localStorage.setItem(STORAGE_KEYS.refreshToken, data.refresh_token);
      localStorage.setItem(STORAGE_KEYS.sessionId, data.session_id);
      localStorage.setItem(
        STORAGE_KEYS.anchorProfile,
        JSON.stringify(data.anchor),
      );
      set({
        anchor: data.anchor,
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        sessionId: data.session_id,
        isAuthenticated: true,
        loading: false,
        error: null,
        restored: true,
      });
      if (options?.reconnectRealtime !== false) {
        syncRealtimeConnection(data.anchor, data.access_token);
      }
      return data.access_token;
    } catch {
      return null;
    }
  },

  restore: () => {
    const accessToken = localStorage.getItem(STORAGE_KEYS.accessToken);
    const refreshToken = localStorage.getItem(STORAGE_KEYS.refreshToken);
    const sessionId = localStorage.getItem(STORAGE_KEYS.sessionId);
    const anchorProfile = localStorage.getItem(STORAGE_KEYS.anchorProfile);

    if (!accessToken || !refreshToken || !sessionId || !anchorProfile) {
      clearStoredSession();
      set({ isAuthenticated: false, loading: false, restored: true });
      return;
    }

    try {
      const anchor = JSON.parse(anchorProfile) as AnchorProfile;
      apiClient.setToken(accessToken);
      set({
        anchor,
        accessToken,
        refreshToken,
        sessionId,
        isAuthenticated: true,
        loading: false,
        error: null,
        restored: true,
      });
      syncRealtimeConnection(anchor, accessToken);
    } catch {
      localStorage.removeItem(STORAGE_KEYS.anchorProfile);
      set({ isAuthenticated: false, loading: false, restored: true });
    }
  },

  updateAnchor: (patch) => {
    set((state) => {
      if (!state.anchor) {
        return state;
      }
      const anchor = { ...state.anchor, ...patch };
      localStorage.setItem(STORAGE_KEYS.anchorProfile, JSON.stringify(anchor));
      syncRealtimeConnection(anchor, state.accessToken);
      return { ...state, anchor };
    });
  },
}));

apiClient.setAuthFailureHandler(() => {
  useAuthStore.getState().handleAuthFailure();
});

apiClient.setAuthRefreshHandler(() => {
  return useAuthStore.getState().refreshSession();
});
